//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { GridOptions, GridController, GridTypes } from '../../../common/grid/grid.controller'

import { IUserSession, TypeHelper, LocalizationHelper, ToastHelper, ConvertHelper, LogHelper } from '../../../../helpers'
import { Enums } from '../../../../models/common'
import { OpConsumerNotificationCollection, OpConsumerNotificationContent } from '../../../../models/custom'

import { ConsumerHelper } from '../../../consumers/consumers.helper'
import { IMRCashierPanelSendNotificationOptions, IIMRCashierPanelSendNotification } from './cashier-panel-send-notification.interfaces'
import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

export class IIMRCashierPanelSendNotificationController implements ng.IComponentController, IIMRCashierPanelSendNotification {

    // Consts

    private static get LOG_PREFIX(): string { return '[SIMAX.Module.CashierPanel.SendNotification]' }
    private get NOTIFICATION_GRID_NAME(): string { return 'sendPanelNotificationGrid' }

    // Consts End


    // Members

    public get notificationMessage(): string {
        if (this.notificationTextArea != null) {
            return this.notificationTextArea.option('value')
        }

        return TypeHelper.emptyString
    }

    public get notificationDeliveryTypes(): Enums.NotificationDeliveryType[] {
        return this._notificationDeliveryTypes
    }

    private options: IMRCashierPanelSendNotificationOptions
    private showLoader: boolean

    private consumerPersonalData: string
    private chosenRecipient: string
    private chosenDeliveryTypes: string
    private _notificationDeliveryTypes: Enums.NotificationDeliveryType[] = []

    private notificationCollection: OpConsumerNotificationCollection
    private notificationCollectionToSend: OpConsumerNotificationCollection
    private chosenDeliveryTypesToShowInPopup: string
    private deliveryTypeToolbarOptions: DevExpress.ui.dxToolbarOptions

    private notificationsFormOptions: DevExpress.ui.dxFormOptions
    private notificationsFormItems: DevExpress.ui.dxFormSimpleItem[]

    private notificationsGridOptions: GridOptions
    private notificationsGrid: GridController<OpConsumerNotificationContent>
    private notificationsGridDataSource: DataSource

    private changeNotificationTypePopupOptions: IMRPopupOptions
    private changeNotificationPopup: IIMRPopup
    private notificationTextArea: DevExpress.ui.dxTextArea


    // Members End


    // Ctor

    constructor(
        private $scope: ng.IScope,
        private userSession: IUserSession
    ) {
        this.initializeBaseOptions()
    }

    // Ctor End    

    // IComponentController Implementation 

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // IComponentController Implementation End


    // Component

    private initializeBaseOptions(): void {
        this.chosenRecipient = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChosenRecipient)
        this.chosenDeliveryTypes = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChosenDeliveryTypes)

    }

    private initialize(): void {

        if (this.options != null) {

            this.showLoader = true

            if (this.options.consumer != null && this.options.consumer.actor != null) {
                this.consumerPersonalData = this.options.consumer.actor.toString()
            }

            $.when(
                this.getNotifications(this.options.consumer.idConsumer).then((result: OpConsumerNotificationContent[]) => {
                    this.notificationsGridDataSource = new DataSource(result)

                    this.setNotificationDeliveryTypes()
                    this.initializeNotificationsGrid()
                })
                    .fail(() => {
                        this.notificationsGridDataSource = null
                    }),

                this.getChangeDeliverytypeButtonOptions(),
                this.getNotificationsFormOptions(),
                this.getChangeNotificationTypePopupOptions()
            ).then(() => {
                this.onInitialized()
                this.showLoader = false
            })
        }
    }

    // Component End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    // Events End 

    private getChangeDeliverytypeButtonOptions(): void {
        let visible: boolean = false
        if (this.options != null && this.options.allowChangeDeliveryType) {
            visible = true
        }

        this.deliveryTypeToolbarOptions = {
            items: [<DevExpress.ui.dxToolbarItem>{
                location: 'before',
                widget: 'dxButton',
                options: <DevExpress.ui.dxButtonOptions>{
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Change),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Change),
                    onClick: (e: any) => {
                        if (this.changeNotificationPopup != null) {
                            this.changeNotificationPopup.show()
                        }
                    }
                },
                visible: visible
            }]
        }
    }

    private getNotifications(idConsumer: number): JQueryPromise<OpConsumerNotificationContent[]> {
        return this.userSession.getDataProvider()
            .Consumer
            .getConsumerNotifications(idConsumer)
            .then((result: OpConsumerNotificationCollection) => {
                this.notificationCollection = result
                this.notificationCollectionToSend = this.notificationCollection.clone(true, false, false, false)

                return result.notifications
            })
    }

    private setNotificationDeliveryTypes(): void {
        this._notificationDeliveryTypes = []

        if (this.notificationCollectionToSend != null && this.notificationCollectionToSend.notifications != null) {
            this.notificationCollectionToSend.notifications.forEach((content: OpConsumerNotificationContent) => {
                if (content.isActive == true) {
                    this._notificationDeliveryTypes.push(content.idNotificationDeliveryType)
                }
            })

            this.chosenDeliveryTypesToShowInPopup = this._notificationDeliveryTypes.map((type: Enums.NotificationDeliveryType) => Enums.NotificationDeliveryType[type]).join(', ')

        }
    }

    private setNotificationsFormItems(): void {
        this.notificationsFormItems = [
            <DevExpress.ui.dxFormSimpleItem>{
                visibleIndex: 1,
                colSpan: 1,
                label: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationText)
                },
                editorType: "dxTextArea",
                editorOptions: <DevExpress.ui.dxTextAreaOptions>{
                    onInitialized: (e: any) => {
                        this.notificationTextArea = e.component
                    },
                    height: 200
                }
            }
        ]
    }

    private getNotificationsFormOptions(): void {
        this.setNotificationsFormItems()

        this.notificationsFormOptions = {
            requiredMark: '*',
            showRequiredMark: true,
            colCount: 1,
            height: '100%',
            width: '100%',
            labelLocation: 'top',
            items: this.notificationsFormItems,
        }
    }

    private initializeNotificationsGrid(): void {
        this.notificationsGridOptions = {
            buttonsOptions: {
                enabled: true,
            },
            columns: ConsumerHelper.getNotificationsGridColumns(),
            name: this.NOTIFICATION_GRID_NAME,
            type: GridTypes.MAIN,
            onInitialized: (e: any, gridController: GridController<OpConsumerNotificationContent>) => { this.notificationsGrid = gridController },
            dataSource: this.notificationsGridDataSource,
            editing: {
                allowUpdating: true,
                mode: "cell",
            },
            ignoreKey: true,
        }
    }

    private getChangeNotificationTypePopupOptions(): void {
        this.changeNotificationTypePopupOptions = {
            innerOptions: {
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Notifications),
                maxWidth: 700,
                height: 450
            },
            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.changeNotificationPopup = e.component
            },
            bottomToolbarOptions: {
                items: [{
                    location: 'after',
                    widget: 'dxButton',
                    options: <DevExpress.ui.dxButtonOptions>{
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
                        onClick: (e: any) => {
                            this.changeNotificationPopup.toggleLoading(true)
                            $.when(
                                this.saveNotifications()
                            ).then(() => {
                                this.changeNotificationPopup.hide()
                            }).always(() => {
                                this.changeNotificationPopup.toggleLoading(false)
                            })
                        }
                    },

                }, {
                    location: 'after',
                    widget: 'dxButton',
                    options: <DevExpress.ui.dxButtonOptions>{
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                        onClick: (e: any) => {
                            if (this.changeNotificationPopup != null) {
                                this.changeNotificationPopup.hide()
                            }
                        }
                    }
                }]
            }
        }
    }

    private saveNotifications(): JQueryPromise<OpConsumerNotificationContent[]> {
        this.setOpStates()

        // Avatar jest nullowany, ponieważ powoduje to problem przy deserializacji.
        if (this.notificationCollection != null && this.notificationCollection.operator != null && this.notificationCollection.operator.actor != null) {
            this.notificationCollection.operator.actor.avatar = null
        }

        return this.userSession.getDataProvider().Consumer.saveConsumerNotifications(this.notificationCollection)
            .then((result: OpConsumerNotificationCollection) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, Enums.SimaxModuleName.Consumer))

                this.notificationCollection = result
                this.notificationCollectionToSend = this.notificationCollection.clone(true, false, false, false)

                return result.notifications
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(ConsumerHelper.LOG_PREFIX, `Unable to save notification ${errorMessage}.`)
            })
            .always(() => {
                this.setNotificationDeliveryTypes()
            })
    }

    private setOpStates(): void {

        let isGridItems: boolean
        let gridItems: OpConsumerNotificationContent[]

        if (this.notificationsGrid != null && this.notificationsGrid.dxGrid.getDataSource().items() != null) {
            isGridItems = true
            gridItems = this.notificationsGrid.dxGrid.getDataSource().items()
        } else {
            isGridItems = false
        }

        if (this.notificationCollectionToSend != null && this.notificationCollectionToSend.notifications != null && isGridItems) {
            const beforeChanges: OpConsumerNotificationContent[] = _.sortBy(this.notificationCollectionToSend.notifications, (notification: OpConsumerNotificationContent) => notification.idNotificationDeliveryType)
            const afterChanges: OpConsumerNotificationContent[] = _.sortBy(gridItems, (notification: OpConsumerNotificationContent) => notification.idNotificationDeliveryType)

            _.forEach(beforeChanges, (beforeChange: OpConsumerNotificationContent, index: number) => {
                const afterChange = afterChanges[index]

                if (beforeChange.isActive !== afterChange.isActive) {
                    afterChange.opState = Enums.OpChangeState.Modified
                }
            })

            this.notificationCollection.notifications = afterChanges
        }
    }

}


